import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import CheckCircleIcon from '@atlaskit/icon/glyph/editor/success';
import { ModalFooter as ModalFooterAtlaskit } from '@atlaskit/modal-dialog';
import { colors } from '@atlaskit/theme';
import { N40A } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
// Disabling this until isGICPositioningEnabled ff is cleaned up
// eslint-disable-next-line jira/import/no-restricted-paths
import { useIsGICPositioningEnabled } from '../../../controllers/gic-positioning-enabled';
// Disabling this until isGICPositioningEnabled ff is cleaned up
import { ISSUE_CREATE_FORM } from '../../constants/selectors';
import { getIssueDiscoveryUrl } from '../../utils/issue-discovery';
import { GIC_VARIANTS } from './constants';
import messages from './messages';
import type { containerStyle, Props } from './types';

const PassThroughWrapper = ({ children }: { children: ReactNode; content?: ReactNode }) => (
	<>{children}</>
);

const ModalFooter = (props: {
	children: ReactNode;
	isMiniVariant: boolean;
	containerStyle?: containerStyle;
}) => {
	const { isMiniVariant, containerStyle, children } = props;
	if (isMiniVariant) {
		/**
		 * We need to use our own footer and not the atlaskit one since the atlaskit one
		 * needs to be within ModalDialog to get context or else it crashes
		 */
		// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		return <FooterMini style={containerStyle}>{children}</FooterMini>;
	}
	return <ModalFooterAtlaskit>{children}</ModalFooterAtlaskit>;
};

export const Footer = (props: Props) => {
	const { formatMessage } = useIntl();
	const {
		onClose,
		onSubmit,
		isSubmitting,
		isOperationInProgress = false,
		onCreateAnotherChange,
		canCreateAnotherIssue,
		shouldDisableSubmit = false,
		createdIssues,
		variant = GIC_VARIANTS.DEFAULT,
		displayCreateAnotherIssueToggle: newDisplayCreateAnotherIssueToggle,
		showIssuesCreated,
		containerStyle,
		showCancelButton,
		showDiscardButton = false,
	} = props;
	const numberOfIssuesCreated = createdIssues.length;

	let displayCreateAnotherIssueToggle = newDisplayCreateAnotherIssueToggle;
	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	const isMiniVariant = isGICPositioningEnabled() && variant === GIC_VARIANTS.MINI;

	if (!isGICPositioningEnabled()) {
		const [{ displayCreateAnotherIssueToggle: oldDisplayCreateAnotherIssueToggle }] =
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal();
		displayCreateAnotherIssueToggle = oldDisplayCreateAnotherIssueToggle;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onViewIssuesLinkClicked = (_: any, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'viewIssue');
	};

	const getPrimaryButtonCopy = () => {
		if (isMiniVariant) {
			return messages.createButtonInMiniView;
		}
		return messages.createButton;
	};

	const IssuesCreatedTooltip = isMiniVariant ? Tooltip : PassThroughWrapper;
	const showIssueCreatedTooltip = Boolean(numberOfIssuesCreated) && Boolean(showIssuesCreated);

	return (
		<ModalFooter isMiniVariant={isMiniVariant} containerStyle={containerStyle}>
			{displayCreateAnotherIssueToggle && (
				<Checkbox
					onChange={onCreateAnotherChange}
					label={formatMessage(
						fg('jira-issue-term-fade-m1')
							? messages.createAnother
							: messages.createAnotherToBeFaded,
					)}
					isChecked={canCreateAnotherIssue}
					isDisabled={shouldDisableSubmit}
					testId="issue-create.common.ui.footer.checkbox"
				/>
			)}
			{showIssueCreatedTooltip && (
				<IssuesCreatedTooltip
					content={formatMessage(messages.issuesCreatedLink, {
						issueCount: numberOfIssuesCreated,
					})}
				>
					<IssueLinkContainer>
						<CheckCircleIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.success', colors.G400)}
						/>
						<Button
							appearance="subtle-link"
							spacing="none"
							href={getIssueDiscoveryUrl(createdIssues)}
							target="_blank"
							onClick={onViewIssuesLinkClicked}
							testId="issue-create.common.ui.footer.created-issue-link"
							interactionName="global-issue.create-modal.created-issue-link"
						>
							{isMiniVariant
								? numberOfIssuesCreated
								: formatMessage(messages.issuesCreatedLink, {
										issueCount: numberOfIssuesCreated,
									})}
						</Button>
					</IssueLinkContainer>
				</IssuesCreatedTooltip>
			)}
			{(!isMiniVariant || showCancelButton) && (
				<CloseButtonContainer>
					<Button
						appearance="subtle-link"
						onClick={onClose}
						isDisabled={isSubmitting}
						testId="issue-create.common.ui.footer.cancel-button"
						interactionName="global-issue.create-modal.cancel"
					>
						{formatMessage(messages.cancelButton)}
					</Button>
				</CloseButtonContainer>
			)}

			{ff('jira-intelligent-issue-breakdown_18syl') && showDiscardButton && (
				<CloseButtonContainer>
					<LoadingButton
						appearance="subtle-link"
						onClick={onClose}
						isLoading={isOperationInProgress}
						isDisabled={isSubmitting || isOperationInProgress}
						testId="issue-create.common.ui.footer.discard-button"
						interactionName="global-issue.create-modal.discard"
					>
						{formatMessage(messages.discardButton)}
					</LoadingButton>
				</CloseButtonContainer>
			)}

			<LoadingButton
				appearance="primary"
				onClick={onSubmit}
				isLoading={isSubmitting || isOperationInProgress}
				isDisabled={isSubmitting || shouldDisableSubmit}
				type="submit"
				form={ISSUE_CREATE_FORM}
				testId="issue-create.common.ui.footer.create-button"
				interactionName="global-issue.create-modal.submit"
			>
				{isGICPositioningEnabled()
					? formatMessage(getPrimaryButtonCopy())
					: formatMessage(messages.createButton)}
			</LoadingButton>
		</ModalFooter>
	);
};

export default Footer;

export { GIC_VARIANTS } from './constants';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonContainer = styled.div<{
	children?: ReactNode;
}>({
	marginLeft: 'auto',
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueLinkContainer = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
	marginLeft: token('space.200', '16px'),
	marginRight: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FooterMini = styled.footer({
	display: 'flex',
	position: 'relative',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.050', '4px'),
	flex: '0 0 auto',
	margin: `0 ${token('space.150', '12px')}`,
	padding: `${token('space.250', '20px')} ${token('space.100', '8px')}`,
	borderTop: `0.5px solid ${token('color.border', N40A)}`,
	boxShadow: `0 -6px 5px -5px ${token('elevation.shadow.overflow.spread', '#091E4229')}`,
});
